import {
    AllNeedingAssignmentQuery,
    AllNeedingAttentionQuery,
    AllVisitsByStateQuery,
} from '@/pages/AllVisitsPage/AllVisits.gql.gql.types';
import { VisitRequestWorkflowStep, VisitState } from '@/types/graphql';
import { Utils } from '@/types/utils';
import {
    GridColDef,
    GridRenderCellParams,
    GridRenderEditCellParams,
    ValueOptions as GridValueOptions,
    GridValueOptionsParams,
} from '@mui/x-data-grid';

export namespace AllVisitsPageNS {
    export const PRIMARY_TABS = {
        [VisitRequestWorkflowStep.NEEDS_SERVICES]: 'Needs Services',
        [VisitRequestWorkflowStep.NEEDS_CLINICIAN]: 'Needs Clinician',
        [VisitRequestWorkflowStep.NEEDS_TENTATIVE_DATE]: 'Needs Date',
        [VisitRequestWorkflowStep.READY_FOR_PROMOTION]: 'Send for Time',
        [VisitState.UNSCHEDULED]: 'Unscheduled',
        [VisitState.SCHEDULED]: 'Scheduled',
        [VisitState.CONFIRMED]: 'Confirmed',
        [VisitState.EN_ROUTE]: 'En Route',
        [VisitState.AT_LOCATION]: 'At Location',
        [VisitState.IN_REVIEW]: 'In Review',
        [VisitState.ENDED]: 'Ended',
    } as const;

    export const WorkflowStepTitles: Record<VisitRequestWorkflowStep, string> = {
        [VisitRequestWorkflowStep.NEEDS_SERVICES]: 'Needs Services',
        [VisitRequestWorkflowStep.NEEDS_CLINICIAN]: 'Needs Clinician',
        [VisitRequestWorkflowStep.NEEDS_TENTATIVE_DATE]: 'Needs Date',
        [VisitRequestWorkflowStep.READY_FOR_PROMOTION]: 'Send for Time',
        [VisitRequestWorkflowStep.COMPLETED]: 'Completed',
        [VisitRequestWorkflowStep.UNKNOWN]: 'Unknown',
    };

    export type PrimaryTab = keyof typeof PRIMARY_TABS;

    export type SecondaryTabConfig = [title: VisitRequestWorkflowStep, isDefaultSelected: boolean];

    export type SecondaryTabConfigSet = Readonly<SecondaryTabConfig>;

    export type TabConfig = {
        tab: PrimaryTab;
        label: string;
        lowerTabs: Set<SecondaryTabConfigSet> | null;
    };

    export type AllNeedingAttentionVisits = NonNullable<
        AllNeedingAttentionQuery['visits']
    >['items'];

    export type AllNeedingAttentionVisitRequests = NonNullable<
        AllNeedingAttentionQuery['visitRequests']
    >['items'];

    type AllNeedingAttentionItem =
        | AllNeedingAttentionVisits[number]
        | AllNeedingAttentionVisitRequests[number];

    export type AllNeedingAttentionItems = AllNeedingAttentionItem[];

    export type AllNeedingAssignmentItems = NonNullable<
        AllNeedingAssignmentQuery['visitRequests']
    >['items'];
    export type AllVisitsByStateItems = NonNullable<AllVisitsByStateQuery['visits']>['items'];

    export type AllVisitsVisit = AllNeedingAttentionVisits | AllVisitsByStateItems;
    export type AllVisitsVisitRequest =
        | AllNeedingAttentionVisitRequests
        | AllNeedingAssignmentItems;

    export type VisitRequestCell = Exclude<AllVisitsVisitRequest[number], Record<string, never>>;
    export type VisitCell = Exclude<AllVisitsVisit[number], Record<string, never>>;

    export type TableData =
        | AllNeedingAttentionItems
        | AllNeedingAssignmentItems
        | AllVisitsByStateItems;

    export type TableRow = TableData[number];
    export type ColDef<T = Utils.UNSAFE_ANY> = GridColDef<AllVisitsPageNS.TableRow, T>;

    export type ColumnDef<T = Utils.UNSAFE_ANY> = {
        def: ColDef<T>;
        tabs: Set<PrimaryTab> | 'all';
    };

    export type RenderEditCell = ColDef['renderEditCell'];
    export type RenderCell = ColDef['renderCell'];
    export type CellClassName = ColDef['cellClassName'];
    export type ValueGetter = ColDef['valueGetter'];
    export type SortComparator<T> = ColDef<T>['sortComparator'];

    export type ValueOptions = (
        params: GridValueOptionsParams<AllVisitsPageNS.TableRow>,
    ) => GridValueOptions[];

    export type CellProps = GridRenderCellParams<TableRow>;
    export type VisitRequestCellProps = GridRenderCellParams<VisitRequestCell>;
    export type VisitCellProps = GridRenderCellParams<VisitCell>;
    export type EditCellProps = GridRenderEditCellParams<TableRow>;
}
